/* Raagas.Shop theme: predictive search via Shopify's /search/suggest.json. */
(() => {
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const formatPrice = (value) => {
    const { currency, locale } = window.theme || {};
    const number = Number(value);
    if (!currency || Number.isNaN(number)) return esc(value);
    try {
      return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : (locale || undefined), {
        style: 'currency', currency, maximumFractionDigits: number % 1 ? 2 : 0
      }).format(number);
    } catch (_) { return esc(value); }
  };

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.predictive-results');
      this.suggest = this.querySelector('.search-suggest');
      if (this.dataset.enabled === 'false' || !this.input || !this.results) return;
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 220);
      });
      this.input.addEventListener('keydown', (e) => this.onKey(e));
    }

    clear() {
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      if (this.suggest) this.suggest.hidden = false;
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.clear(); return; }
      this.controller?.abort();
      this.controller = new AbortController();
      const base = ((window.theme && window.theme.routes && window.theme.routes.predictive_search_url) || '/search/suggest').replace(/\.json$/, '');
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,article,page',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last'
      });
      try {
        const res = await fetch(`${base}.json?${params}`, { signal: this.controller.signal });
        const data = await res.json();
        this.render(data.resources && data.resources.results ? data.resources.results : {}, q);
      } catch (err) {
        if (err.name !== 'AbortError') this.clear();
      }
    }

    render(results, q) {
      const products = results.products || [];
      const others = [...(results.collections || []), ...(results.articles || []), ...(results.pages || [])];
      if (this.suggest) this.suggest.hidden = true;
      if (!products.length && !others.length) {
        this.results.innerHTML = `<p class="text-muted" role="status">${esc((window.theme.strings || {}).noResults || 'No results')}</p>`;
        return;
      }
      const productHtml = products.map((p) => `
        <li><a href="${esc(p.url)}" role="option">
          ${p.image ? `<img src="${esc(p.image)}" alt="" width="56" height="56" loading="lazy">` : '<span class="placeholder" style="width:3.5rem;height:3.5rem;border-radius:8px"></span>'}
          <span>${esc(p.title)}</span>
          <span class="price">${formatPrice(p.price)}</span>
        </a></li>`).join('');
      const otherHtml = others.map((o) => `
        <li><a href="${esc(o.url)}" role="option"><span class="placeholder" style="width:3.5rem;height:3.5rem;border-radius:8px"></span><span>${esc(o.title)}</span><span></span></a></li>`).join('');
      this.results.innerHTML = `<ul role="presentation">${productHtml}${otherHtml}</ul>
        <p class="mt-4"><a class="link link--arrow" href="/search?q=${encodeURIComponent(q)}">${esc(q)} &rarr;</a></p>`;
      this.input.setAttribute('aria-expanded', 'true');
    }

    onKey(e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const items = Array.from(this.results.querySelectorAll('a[role="option"]'));
      if (!items.length) return;
      e.preventDefault();
      const i = items.findIndex((a) => a.getAttribute('aria-selected') === 'true');
      items.forEach((a) => a.removeAttribute('aria-selected'));
      const next = items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
      next.setAttribute('aria-selected', 'true');
      next.focus();
      next.addEventListener('blur', () => next.removeAttribute('aria-selected'), { once: true });
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
})();
